import { z } from 'zod';
import axiosInstance from '@/lib/axiosInstance';
import { Classroom } from '@/model/Classroom.model';
import { getPaginatedItemsSchema, Paginated, PaginationQueryParams } from '@/model/common/pagination.model';
import { SortDirection, SortingQueryParams } from '@/model/common/sorting.model';
import { School } from '@/model/School/School.model';
import { SchoolGroup } from '@/model/SchoolGroup.model';
import { User, UserRole } from '@/model/User.model';

export const classroomSummarySchema = z.object({
  schoolId: z.string(),
  id: z.string(),
  name: z.string(),
  completed: z.number(),
  total: z.number(),
  score: z.nullable(z.number().optional()),
});

const schoolWithClassroomSummarySchema = z.object({
  school: School.zodSchema,
  classroomsSummaries: z.array(classroomSummarySchema),
});

export type ClassroomSummary = z.infer<typeof classroomSummarySchema>;
export type SchoolWithClassroomSummary = z.infer<typeof schoolWithClassroomSummarySchema>;

interface SchoolWithClassroom {
  school: School;
  classrooms: Classroom[];
}

export interface getUserInSchoolGroupFilters {
  role: UserRole;
  firstname?: string;
  lastname?: string;
  classroomIds?: string[];
}

export const getUserInSchoolGroup = async (
  filters: getUserInSchoolGroupFilters,
  query: PaginationQueryParams & SortingQueryParams
): Promise<Paginated<User>> => {
  const queryParams = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    if (value?.length > 0) {
      queryParams.append(key, value);
    }
  }

  queryParams.append('page', query.page.toString());
  queryParams.append('pageSize', query.pageSize.toString());
  queryParams.append('sortDirection', query.sortDirection);
  queryParams.append('sortBy', query.sortBy);
  const { data } = await axiosInstance.get(`/school-groups/user`, {
    params: queryParams,
  });

  return getPaginatedItemsSchema(User.zodSchema).parse(data);
};

export const getSchoolGroupBySchoolId = async (id: string) => {
  const { data } = await axiosInstance.get(`/school-groups/school/${id}`);
  return SchoolGroup.fromJson(data);
};

export const getSchoolWithClassroomSummaryInSchoolGroup = async () => {
  const { data } = await axiosInstance.get(`/school-groups/classroom-summary`);
  const validJson = z.array(schoolWithClassroomSummarySchema).parse(data);

  const parsedData = validJson.map((d) => {
    return {
      school: School.fromJson(d.school),
      classroomsSummaries: d.classroomsSummaries,
    };
  });

  return parsedData;
};

export const getSchoolWithClassroomInSchoolGroup = async (): Promise<SchoolWithClassroom[]> => {
  const { data } = await axiosInstance.get(`/school-groups/school-with-classroom`);
  return data;
};

export const getOwnSchoolGroup = async () => {
  const { data } = await axiosInstance.get(`/school-groups/me`);
  return SchoolGroup.fromJson(data);
};

export const getSchoolGroups = async () => {
  const { data } = await axiosInstance.get(`/school-groups`, {
    params: {
      page: 1,
      pageSize: 1000, // XXX: Adjust page size as needed
      sortDirection: SortDirection.Asc,
      sortBy: 'name',
    },
  });

  return SchoolGroup.fromJsonArray(data);
};
